import React from 'react';
import { useTranslation } from 'react-i18next';
import LegalPage from './LegalPage';

function Terms() {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const content = isEnglish ? (
    <>
      <h2>1. Acceptance of Terms</h2>
      <p>By accessing and using the Alhamdulillah mobile application ("the App"), you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the App.</p>

      <h2>2. Description of Service</h2>
      <p>Alhamdulillah is a spiritual wellness application that helps users practice daily gratitude through voice recordings, journaling, emotional tracking, and Islamic dhikr (remembrance). The App is designed to support personal spiritual growth and mindfulness.</p>

      <h2>3. Pricing and Subscriptions</h2>
      <h3>3.1 Free Version</h3>
      <p>Alhamdulillah offers a free version with full core functionality including:</p>
      <ul>
        <li>Voice recording of gratitude moments</li>
        <li>Daily emotional tracking</li>
        <li>Gratitude journal</li>
        <li>Access to 8 essential dhikr</li>
        <li>Basic reminders</li>
        <li>Cloud synchronization</li>
      </ul>

      <h3>3.2 Premium Subscriptions (Optional)</h3>
      <p><strong>All subscriptions are completely optional.</strong> The free version provides full access to core features.</p>

      <p><strong>Monthly Premium: €5.99/month</strong></p>
      <ul>
        <li>All free features plus:</li>
        <li>Unlimited voice recordings</li>
        <li>Advanced emotional analytics</li>
        <li>Custom reminders</li>
        <li>Priority support</li>
        <li>Ad-free experience</li>
      </ul>

      <p><strong>Lifetime Premium: €24.99 (one-time payment)</strong></p>
      <ul>
        <li>All monthly premium features</li>
        <li>Pay once, use forever</li>
        <li>Lifetime updates and all future features</li>
        <li>Premium badges</li>
        <li>Priority support forever</li>
      </ul>

      <h3>3.3 Payment and Billing</h3>
      <ul>
        <li>Payments are processed through the App Store (iOS) or Google Play Store (Android)</li>
        <li>Monthly subscriptions auto-renew unless cancelled before the renewal date</li>
        <li>Lifetime purchases are one-time payments with no recurring charges</li>
        <li>Prices are in Euros (€) and may vary by region</li>
        <li>You can cancel subscriptions at any time through your App Store or Google Play account settings</li>
      </ul>

      <h3>3.4 Refunds</h3>
      <p>Refund policies are governed by the App Store or Google Play Store terms. Please contact Apple or Google directly for refund requests. We do not have direct access to process refunds for in-app purchases.</p>

      <h2>4. User Account and Responsibilities</h2>
      <h3>4.1 Account Creation</h3>
      <p>You may need to create an account to access certain features. You are responsible for:</p>
      <ul>
        <li>Maintaining the confidentiality of your account credentials</li>
        <li>All activities that occur under your account</li>
        <li>Notifying us immediately of any unauthorized use</li>
      </ul>

      <h3>4.2 Acceptable Use</h3>
      <p>You agree to use the App only for lawful purposes and in accordance with these Terms. You must not:</p>
      <ul>
        <li>Use the App in any way that violates applicable laws or regulations</li>
        <li>Attempt to gain unauthorized access to the App or its systems</li>
        <li>Upload or transmit viruses, malware, or malicious code</li>
        <li>Interfere with or disrupt the App's functionality</li>
        <li>Use the App to harass, abuse, or harm others</li>
        <li>Impersonate any person or entity</li>
      </ul>

      <h2>5. Privacy and Data Protection</h2>
      <p>Your privacy is paramount to us. All personal data, including voice recordings, journal entries, and emotional tracking data, are:</p>
      <ul>
        <li>Encrypted end-to-end during transmission and storage</li>
        <li>Never shared, sold, or disclosed to third parties</li>
        <li>Never used for AI training or content analysis</li>
        <li>Accessible only to you through your authenticated account</li>
      </ul>
      <p>Please refer to our Privacy Policy for detailed information about how we collect, use, and protect your data.</p>

      <h2>6. Intellectual Property Rights</h2>
      <h3>6.1 Our Content</h3>
      <p>The App, including all content, features, dhikr texts, design elements, and software, is owned by Alhamdulillah and is protected by international copyright and intellectual property laws.</p>

      <h3>6.2 Your Content</h3>
      <p>You retain full ownership of all content you create using the App (voice recordings, journal entries, emotional data). By using the App, you grant us a limited license to store and process this content solely to provide App functionality.</p>

      <h2>7. Disclaimers and Limitations</h2>
      <h3>7.1 Religious Content</h3>
      <p>The dhikr and Islamic content provided in the App are for informational and spiritual practice purposes. While we strive for accuracy, users should consult qualified Islamic scholars for religious guidance. We are not responsible for religious interpretations or decisions made based on App content.</p>

      <h3>7.2 Not Medical or Therapeutic Service</h3>
      <p>Alhamdulillah is a wellness and spiritual app, not a substitute for professional medical, psychological, or therapeutic services. If you are experiencing mental health issues, please consult qualified healthcare professionals.</p>

      <h3>7.3 App Availability</h3>
      <p>We strive to keep the App available at all times, but we do not guarantee uninterrupted access. The App may be unavailable due to maintenance, updates, or technical issues.</p>

      <h2>8. Termination</h2>
      <p>We may suspend or terminate your access to the App at any time for violations of these Terms. You may delete your account at any time through the App settings. Upon termination:</p>
      <ul>
        <li>Your right to use the App immediately ceases</li>
        <li>You should delete the App from all devices</li>
        <li>Your personal data will be handled according to our Privacy Policy</li>
        <li>Paid subscriptions may not be refunded (subject to App Store/Google Play policies)</li>
      </ul>

      <h2>9. Changes to Terms</h2>
      <p>We reserve the right to modify these Terms at any time. Changes will be effective upon posting in the App or on our website. Your continued use after changes constitutes acceptance of the modified Terms.</p>

      <h2>10. Limitation of Liability</h2>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE APP.</p>

      <h2>11. Governing Law</h2>
      <p>These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.</p>

      <h2>12. Contact Information</h2>
      <p>For questions about these Terms of Use, please contact us through the App's support section or website.</p>

      <h2>13. Entire Agreement</h2>
      <p>These Terms, together with our Privacy Policy and EULA, constitute the entire agreement between you and Alhamdulillah regarding the use of the App.</p>
    </>
  ) : (
    <>
      <h2>1. Acceptation des Conditions</h2>
      <p>En accédant et en utilisant l'application mobile Alhamdulillah ("l'Application"), vous acceptez et vous engagez à être lié par ces Conditions d'Utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser l'Application.</p>

      <h2>2. Description du Service</h2>
      <p>Alhamdulillah est une application de bien-être spirituel qui aide les utilisateurs à pratiquer la gratitude quotidienne à travers des enregistrements vocaux, un journal, un suivi émotionnel et du dhikr islamique (rappel). L'Application est conçue pour soutenir la croissance spirituelle personnelle et la pleine conscience.</p>

      <h2>3. Tarification et Abonnements</h2>
      <h3>3.1 Version Gratuite</h3>
      <p>Alhamdulillah offre une version gratuite avec toutes les fonctionnalités principales incluant :</p>
      <ul>
        <li>Enregistrement vocal des moments de gratitude</li>
        <li>Suivi émotionnel quotidien</li>
        <li>Journal de gratitude</li>
        <li>Accès à 8 dhikr essentiels</li>
        <li>Rappels de base</li>
        <li>Synchronisation cloud</li>
      </ul>

      <h3>3.2 Abonnements Premium (Optionnels)</h3>
      <p><strong>Tous les abonnements sont complètement optionnels.</strong> La version gratuite fournit un accès complet aux fonctionnalités principales.</p>

      <p><strong>Premium Mensuel : 5,99€/mois</strong></p>
      <ul>
        <li>Toutes les fonctionnalités gratuites plus :</li>
        <li>Enregistrements vocaux illimités</li>
        <li>Analyses émotionnelles avancées</li>
        <li>Rappels personnalisés</li>
        <li>Support prioritaire</li>
        <li>Expérience sans publicité</li>
      </ul>

      <p><strong>Premium à Vie : 24,99€ (paiement unique)</strong></p>
      <ul>
        <li>Toutes les fonctionnalités premium mensuelles</li>
        <li>Payez une fois, utilisez pour toujours</li>
        <li>Mises à jour à vie et toutes les futures fonctionnalités</li>
        <li>Badges premium</li>
        <li>Support prioritaire à vie</li>
      </ul>

      <h3>3.3 Paiement et Facturation</h3>
      <ul>
        <li>Les paiements sont traités via l'App Store (iOS) ou Google Play Store (Android)</li>
        <li>Les abonnements mensuels se renouvellent automatiquement sauf annulation avant la date de renouvellement</li>
        <li>Les achats à vie sont des paiements uniques sans frais récurrents</li>
        <li>Les prix sont en Euros (€) et peuvent varier selon la région</li>
        <li>Vous pouvez annuler les abonnements à tout moment via les paramètres de votre compte App Store ou Google Play</li>
      </ul>

      <h3>3.4 Remboursements</h3>
      <p>Les politiques de remboursement sont régies par les conditions de l'App Store ou du Google Play Store. Veuillez contacter Apple ou Google directement pour les demandes de remboursement. Nous n'avons pas d'accès direct pour traiter les remboursements des achats intégrés.</p>

      <h2>4. Compte Utilisateur et Responsabilités</h2>
      <h3>4.1 Création de Compte</h3>
      <p>Vous devrez peut-être créer un compte pour accéder à certaines fonctionnalités. Vous êtes responsable de :</p>
      <ul>
        <li>Maintenir la confidentialité de vos identifiants de compte</li>
        <li>Toutes les activités qui se produisent sous votre compte</li>
        <li>Nous informer immédiatement de toute utilisation non autorisée</li>
      </ul>

      <h3>4.2 Utilisation Acceptable</h3>
      <p>Vous acceptez d'utiliser l'Application uniquement à des fins légales et conformément à ces Conditions. Vous ne devez pas :</p>
      <ul>
        <li>Utiliser l'Application d'une manière qui viole les lois ou réglementations applicables</li>
        <li>Tenter d'obtenir un accès non autorisé à l'Application ou à ses systèmes</li>
        <li>Télécharger ou transmettre des virus, malwares ou code malveillant</li>
        <li>Interférer avec ou perturber la fonctionnalité de l'Application</li>
        <li>Utiliser l'Application pour harceler, abuser ou nuire à autrui</li>
        <li>Usurper l'identité de toute personne ou entité</li>
      </ul>

      <h2>5. Confidentialité et Protection des Données</h2>
      <p>Votre vie privée est primordiale pour nous. Toutes les données personnelles, y compris les enregistrements vocaux, les entrées de journal et les données de suivi émotionnel, sont :</p>
      <ul>
        <li>Chiffrées de bout en bout pendant la transmission et le stockage</li>
        <li>Jamais partagées, vendues ou divulguées à des tiers</li>
        <li>Jamais utilisées pour la formation d'IA ou l'analyse de contenu</li>
        <li>Accessibles uniquement à vous via votre compte authentifié</li>
      </ul>
      <p>Veuillez consulter notre Politique de Confidentialité pour des informations détaillées sur la façon dont nous collectons, utilisons et protégeons vos données.</p>

      <h2>6. Droits de Propriété Intellectuelle</h2>
      <h3>6.1 Notre Contenu</h3>
      <p>L'Application, y compris tout le contenu, les fonctionnalités, les textes de dhikr, les éléments de conception et le logiciel, appartient à Alhamdulillah et est protégée par les lois internationales sur le droit d'auteur et la propriété intellectuelle.</p>

      <h3>6.2 Votre Contenu</h3>
      <p>Vous conservez la pleine propriété de tout le contenu que vous créez en utilisant l'Application (enregistrements vocaux, entrées de journal, données émotionnelles). En utilisant l'Application, vous nous accordez une licence limitée pour stocker et traiter ce contenu uniquement pour fournir la fonctionnalité de l'Application.</p>

      <h2>7. Dénis de Responsabilité et Limitations</h2>
      <h3>7.1 Contenu Religieux</h3>
      <p>Le dhikr et le contenu islamique fournis dans l'Application sont à des fins d'information et de pratique spirituelle. Bien que nous nous efforcions d'assurer l'exactitude, les utilisateurs doivent consulter des érudits islamiques qualifiés pour des conseils religieux. Nous ne sommes pas responsables des interprétations religieuses ou des décisions prises sur la base du contenu de l'Application.</p>

      <h3>7.2 Pas un Service Médical ou Thérapeutique</h3>
      <p>Alhamdulillah est une application de bien-être et spirituelle, et non un substitut aux services médicaux, psychologiques ou thérapeutiques professionnels. Si vous rencontrez des problèmes de santé mentale, veuillez consulter des professionnels de la santé qualifiés.</p>

      <h3>7.3 Disponibilité de l'Application</h3>
      <p>Nous nous efforçons de maintenir l'Application disponible en permanence, mais nous ne garantissons pas un accès ininterrompu. L'Application peut être indisponible en raison de maintenance, de mises à jour ou de problèmes techniques.</p>

      <h2>8. Résiliation</h2>
      <p>Nous pouvons suspendre ou résilier votre accès à l'Application à tout moment en cas de violation de ces Conditions. Vous pouvez supprimer votre compte à tout moment via les paramètres de l'Application. À la résiliation :</p>
      <ul>
        <li>Votre droit d'utiliser l'Application cesse immédiatement</li>
        <li>Vous devez supprimer l'Application de tous les appareils</li>
        <li>Vos données personnelles seront traitées conformément à notre Politique de Confidentialité</li>
        <li>Les abonnements payés peuvent ne pas être remboursés (sous réserve des politiques App Store/Google Play)</li>
      </ul>

      <h2>9. Modifications des Conditions</h2>
      <p>Nous nous réservons le droit de modifier ces Conditions à tout moment. Les modifications prendront effet lors de leur publication dans l'Application ou sur notre site web. Votre utilisation continue après les modifications constitue l'acceptation des Conditions modifiées.</p>

      <h2>10. Limitation de Responsabilité</h2>
      <p>DANS LA MESURE MAXIMALE AUTORISÉE PAR LA LOI, NOUS NE SERONS PAS RESPONSABLES DE TOUT DOMMAGE INDIRECT, ACCESSOIRE, SPÉCIAL, CONSÉCUTIF OU PUNITIF, OU DE TOUTE PERTE DE PROFITS, REVENUS, DONNÉES OU UTILISATION, DÉCOULANT DE OU LIÉ À VOTRE UTILISATION DE L'APPLICATION.</p>

      <h2>11. Droit Applicable</h2>
      <p>Ces Conditions seront régies et interprétées conformément aux lois applicables, sans égard aux principes de conflit de lois.</p>

      <h2>12. Coordonnées</h2>
      <p>Pour toute question sur ces Conditions d'Utilisation, veuillez nous contacter via la section support de l'Application ou le site web.</p>

      <h2>13. Intégralité de l'Accord</h2>
      <p>Ces Conditions, ainsi que notre Politique de Confidentialité et CLUF, constituent l'intégralité de l'accord entre vous et Alhamdulillah concernant l'utilisation de l'Application.</p>
    </>
  );

  return (
    <LegalPage
      title={isEnglish ? 'Terms of Use' : "Conditions d'Utilisation"}
      content={content}
    />
  );
}

export default Terms;
